import { useAuth } from "@/contexts/AuthContext";
import { Track } from "@/types";
import { useMusic } from "@/contexts/MusicContext";
import { useVisitorStreamLimit } from "@/hooks/useVisitorStreamLimit";
import { useSponsorStreamLimit } from "@/hooks/useSponsorStreamLimit";
import { useArtistStreamLimit } from "@/hooks/useArtistStreamLimit";
import { useFanStreamLimit } from "@/hooks/useFanStreamLimit";
import { useStreamTracking } from "@/hooks/useStreamTracking";
import { useStreamProcessor } from "@/hooks/useStreamProcessor";
import { useEffect } from "react";
import VisitorPreviewLimitDialog from "./VisitorPreviewLimitDialog";
import ArtistPreviewLimitDialog from "./ArtistPreviewLimitDialog";

interface TrackStreamDetectorProps {
  track: Track;
  isPlaying: boolean;
  onStreamComplete?: () => void;
  onStreamRecorded?: () => void;
}

export default function TrackStreamDetector({
  track,
  isPlaying,
  onStreamComplete,
  onStreamRecorded,
}: TrackStreamDetectorProps) {
  const { user, userRole } = useAuth();
  const { setIsPlaying, setCurrentTrack } = useMusic();

  // Handle visitor stream limits (29 second preview) - also applies to OAuth users without a role
  const { visitorLimitReached, showPreviewDialog, dismissPreviewDialog } =
    useVisitorStreamLimit({
      isPlaying,
      user,
      userRole,
      trackId: track.id,
    });

  // Handle sponsor stream limits (30 second preview)
  const { sponsorLimitReached } = useSponsorStreamLimit({
    isPlaying,
    user,
    userRole,
    trackId: track.id,
  });

  // Handle artist stream limits (30 second preview for own + other artists' tracks)
  const {
    artistLimitReached,
    showPreviewDialog: showArtistDialog,
    dismissPreviewDialog: dismissArtistDialog,
    isSelfStream,
  } = useArtistStreamLimit({
    isPlaying,
    user,
    userRole,
    trackId: track.id,
    trackArtistId: track.artistId,
  });

  // Only track streams for logged-in users who haven't hit visitor, sponsor, or artist limit
  const canTrackStream =
    user && !visitorLimitReached && !sponsorLimitReached && !artistLimitReached;

  // Track stream duration for logged-in users
  const { streamDuration } = useStreamTracking({
    isPlaying,
    trackId: track.id,
    canTrack: canTrackStream,
  });

  // Handle fan daily stream limits - this will stop playback if limit reached
  const { fanLimitReached } = useFanStreamLimit({
    user,
    userRole,
    trackId: track.id,
    streamDuration,
  });

  // Process streams only if fan limit hasn't been reached
  const canProcessStream = canTrackStream && !fanLimitReached;

  const { streamRecorded, streamInProgress } = useStreamProcessor({
    user,
    userRole,
    track,
    streamDuration,
    canProcess: canProcessStream,
    onStreamRecorded,
  });

  // Stop playback immediately if fan limit is reached during playback
  useEffect(() => {
    if (fanLimitReached && isPlaying) {
      console.log("🚫 Fan limit reached, stopping playback");
      setIsPlaying(false);
      setTimeout(() => {
        setCurrentTrack(null);
      }, 1000);
    }
  }, [fanLimitReached, isPlaying, setIsPlaying, setCurrentTrack]);

  console.log("🎯 TrackStreamDetector state:", {
    user: !!user,
    userRole,
    trackId: track.id,
    isPlaying,
    visitorLimitReached,
    sponsorLimitReached,
    artistLimitReached,
    fanLimitReached,
    streamDuration,
    canTrackStream,
    canProcessStream,
    streamRecorded,
    streamInProgress,
  });

  return (
    <>
      <VisitorPreviewLimitDialog
        open={showPreviewDialog}
        onDismiss={dismissPreviewDialog}
        trackTitle={track.title}
        artistName={track.artist}
        coverUrl={track.coverUrl}
      />
      <ArtistPreviewLimitDialog
        open={showArtistDialog}
        onDismiss={dismissArtistDialog}
        trackTitle={track.title}
        artistName={track.artist}
        coverUrl={track.coverUrl}
        artistUsername={track.artistUsername}
        isSelfStream={isSelfStream}
      />
    </>
  );
}
